<template>
    <v-treeview
        :items="sortedItems"
        activatable
        :activated="active"
        item-value="id"
        @update:activated="updateActive"
        :opened="open"
        @update:opened="onOpenedUpdate"
        color="secondary"
        expand-icon="mdi-chevron-down"
        :load-children="loadChildren"
    >
        <template v-slot:prepend="{ isOpen, item, isLeaf }">
            <div :id="item.id">
                <v-icon v-if="!isLeaf" color="accent">
                    {{
                        isOpen
                            ? 'mdi-folder-open-outline'
                            : 'mdi-folder-outline'
                    }}
                </v-icon>
                <v-icon v-else :style="show[item.calcId] && 'margin-top: 10px'">
                    {{ 'mdi-vector-polyline mdi-rotate-90' }}
                </v-icon>
            </div>
        </template>

        <template v-slot:title="{ item, isLeaf }">
            <v-container>
                <v-row>
                    <v-col v-if="!isLeaf">
                        <span>
                            {{ item.name }}
                            <span v-if="item.count"> ({{ item.count }}) </span>
                        </span>
                    </v-col>
                    <v-col v-if="isLeaf">
                        <div style="display: flex">
                            <div style="flex-grow: 1">
                                <div>
                                    <router-link
                                        :to="{
                                            name: 'calculation',
                                            params: { calcId: item.calcId },
                                        }"
                                        style="
                                            text-decoration: none;
                                            color: inherit;
                                        "
                                        @click="goneToCalc = true"
                                        >{{ item.name }}</router-link
                                    >
                                </div>
                                <div>
                                    <span
                                        v-if="item.description"
                                        class="
                                            calc-description
                                            text-medium-emphasis
                                            caption
                                        "
                                    >
                                        {{ item.description }}
                                    </span>
                                </div>
                            </div>

                            <div style="white-space: nowrap; text-align: right">
                                <CalcStatsChips
                                    v-if="item.calcId"
                                    :calcId="item.calcId"
                                    :persistentCalcId="item.persistentCalcID"
                                    :moveToEnd="true"
                                    :noRightMargin="true"
                                    :noWrap="true"
                                    @toggle="like"
                                />
                                <div>
                                    <v-btn
                                        class="text-capitalize button"
                                        rounded
                                        size="small"
                                        variant="outlined"
                                        @click="switchInfoShow(item.calcId)"
                                    >
                                        Info
                                        <v-icon class="icon" color="secondary">
                                            mdi-information-outline
                                        </v-icon>
                                    </v-btn>
                                    <v-btn
                                        class="text-capitalize button ml-2"
                                        rounded
                                        size="small"
                                        variant="outlined"
                                        @click="goToCalc(item)"
                                    >
                                        Open
                                        <v-icon class="icon" color="secondary">
                                            mdi-chevron-right
                                        </v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-container>

            <div v-if="show[item.calcId]" style="max-width: 1050px">
                <CalcInfo
                    :show="show[item.calcId]"
                    :calc="item"
                    :libraryVersion="getLibraryVersion(item)"
                />
            </div>
        </template>
    </v-treeview>
</template>
<script setup>
import CalcInfo from '../shared/CalcInfo.vue'
import CalcStatsChips from '../pages/calc/calc-info/CalcStatsChips.vue'
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import helpers from '../../helpers'

const props = defineProps({
    items: { required: true, type: Array },
    url: { required: false, default: false },
    firstOpen: { required: false, type: Array, default: () => [] },
    firstShow: { required: true },
    libraryVersions: { required: true },
    latest: { type: Boolean, required: true },
    calcs: { type: Array, required: true },
})

const store = useStore()
const router = useRouter()
const route = useRoute()

const open = ref([...props.firstOpen])
const active = ref([])
const lastCalcClosed = ref(null)
const show = ref({ ...props.firstShow })
const goneToCalc = ref(false)
const likeClicked = ref(false)
const noCalcsError = ref(false)

const sortedItems = computed(() => sortItems(props.items))

onMounted(() => {
    if (open.value.length > 1) {
        let item = open.value[open.value.length - 1]
        setTimeout(() => {
            const yOffset = -100
            const element = document.getElementById(item.toString())
            if (!element) return
            const y =
                element.getBoundingClientRect().top +
                window.pageYOffset +
                yOffset
            window.scrollTo({ top: y, behavior: 'smooth' })
        }, 500)
    }
})

function like() {
    likeClicked.value = true
}

function getLibraryVersion(calc) {
    return props.libraryVersions.find(
        (vers) => vers.libraryVersionId === calc.libraryVersionId
    )
}

function goToCalc(calc) {
    goneToCalc.value = true
    if (props.latest && calc.persistentCalcID) {
        router.push({
            name: 'persistentCalcId',
            params: { persistentId: calc.persistentCalcID },
        })
    } else {
        router.push({ name: 'calculation', params: { calcId: calc.calcId } })
    }
}

function switchInfoShow(id) {
    let dict = show.value
    dict[id] = !dict[id]
    if (!dict[id]) lastCalcClosed.value = id
    show.value = { ...dict }
    active.value = []
}

function updateActive(a) {
    if (likeClicked.value) {
        likeClicked.value = false
        active.value = []
        return
    }
    if (a[0]) {
        let calcId = a[0].split('-')[1]
        let fullCalc = props.calcs.find((c) => c.id === a[0])
        if (show.value[calcId]) {
            active.value = []
        } else if (parseInt(lastCalcClosed.value) === parseInt(calcId)) {
            active.value = []
            lastCalcClosed.value = null
        } else if (!goneToCalc.value) {
            goToCalc(fullCalc)
        }
    }
}

async function loadChildren(vers) {
    let calcs = []
    await store
        .dispatch('getVersionCalc', vers.libraryVersionId)
        .then((response) => {
            calcs = response
        })
    if (calcs.length > 0) {
        noCalcsError.value = false
        vers.children = helpers.buildTree(calcs)
        helpers.addCount(vers)
        calcs.forEach((calc) => {
            show.value[calc.calcId] = false
        })
        vers.children.forEach((node) => {
            helpers.addCount(node)
        })
        return vers
    } else {
        noCalcsError.value = true
        store.commit('SET_ALERT_ERRORS', [
            `The library version you're trying to use has no calcs. Please either check with the library owner, or ensure the library was uploaded in the right format.`,
        ])
    }
}

function onOpenedUpdate(opened) {
    open.value = opened
    updateUrl(opened)
}

function updateUrl(openArr) {
    if (openArr.length && props.url) {
        let opens = [...openArr]
        if (route.query.path && route.query.path.split('-')[0] === 'latest') {
            opens[0] = 'latest'
        }
        let path
        let lastOpenParts = opens[opens.length - 1]
        if (opens.length === 1) {
            path = opens[0]
        } else {
            let endPath = lastOpenParts
                .substring(lastOpenParts.indexOf('/') + 1)
                .replaceAll('/', '-')
            path = `${opens[0]}-${endPath}`
        }
        if (route.query.path !== path) {
            router.replace({ name: 'libraryBrowser', query: { path } })
        }
    }
}

function sortItems(items) {
    return items
        .map((item) => {
            if (item.children && item.children.length > 0) {
                item.children = sortItems(item.children)
            }
            return item
        })
        .sort((a, b) => {
            const isFolderA = a.children && a.children.length > 0
            const isFolderB = b.children && b.children.length > 0
            if (isFolderA && !isFolderB) return -1
            if (!isFolderA && isFolderB) return 1
            return a.name.localeCompare(b.name)
        })
}
</script>

<style>
/* Vuetify 3 treeview classes (replaces v2 v-treeview-node__* classes) */
.v-treeview-item {
    box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%),
        0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);
    margin-bottom: 5px;
}
.v-treeview-item .v-list-item__content {
    align-items: initial;
}
.v-treeview-item .v-list-item-title {
    white-space: normal;
}
.v-treeview-item .v-list-item__prepend {
    margin-top: 12px;
}
.button {
}
.icon {
    margin-left: 5px;
}
.inputs {
    margin-right: 5px;
}

/* Scoped style for description truncation and margin */
.calc-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 5px;
}
</style>
